// ─── Custom Elements ───────────────────────────────────────────────────────
// NOTE: All custom element definitions are guarded with customElements.get()
// to avoid conflicts with the base theme's (Shopify Saver) module-based
// component system. The base theme's ES modules may register the same tag
// names — if they already exist, we skip our simpler fallback versions.

class HomenomStickyHeader extends HTMLElement {
  connectedCallback() {
    window.addEventListener('scroll', () => {
      this.classList.toggle('scrolled-past-header', window.scrollY > 50);
      if (this.querySelector('header')) {
        this.style.position = window.scrollY > 50 ? 'sticky' : 'relative';
        this.style.top = '0';
      }
    });
  }
}
if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', HomenomStickyHeader);
}

class HomenomMenuDrawer extends HTMLElement {
  constructor() {
    super();
    this._onOpenClick = this._onOpenClick.bind(this);
  }

  connectedCallback() {
    this.setAttribute('aria-hidden', this.getAttribute('aria-hidden') || 'true');

    // Use delegated listener on document for the open button to be robust against section reloads
    document.addEventListener('click', this._onOpenClick);

    // Internal close buttons can use direct listeners as they are part of this element
    this.querySelectorAll('[data-menu-close]').forEach(btn =>
      btn.addEventListener('click', () => this.close())
    );

    // Tab switching
    this.querySelectorAll('.homenom-menu-drawer__tab').forEach(tab => {
      tab.addEventListener('click', () => {
        this.querySelectorAll('.homenom-menu-drawer__tab, .homenom-menu-drawer__tab-content').forEach(el => el.classList.remove('active'));
        tab.classList.add('active');
        this.querySelector(`[data-tab-content="${tab.dataset.tab}"]`)?.classList.add('active');
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onOpenClick);
  }

  _onOpenClick(e) {
    const btn = e.target.closest('[data-menu-open]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      this.open();
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.querySelectorAll('[data-menu-open]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));
  }
}
if (!customElements.get('homenom-menu-drawer')) {
  customElements.define('homenom-menu-drawer', HomenomMenuDrawer);
}

class HomenomCartDrawer extends HTMLElement {
  constructor() {
    super();
    this._onOpenClick = this._onOpenClick.bind(this);
  }

  connectedCallback() {
    this.setAttribute('aria-hidden', this.getAttribute('aria-hidden') || 'true');

    // Use delegated listener on document for the open button to be robust against section reloads
    document.addEventListener('click', this._onOpenClick);

    this.querySelectorAll('[data-cart-close], [data-close-cart]').forEach(btn =>
      btn.addEventListener('click', () => this.close())
    );

    // Listen for base theme's cart events fired by product-form-component
    this._onCartUpdate = (e) => {
      if (typeof fetchCart === 'function') fetchCart();
      this.open();
    };
    document.addEventListener('cart-update', this._onCartUpdate);

    // Also listen for the dispatch-open-cart event (fired by some base theme components)
    this._onDispatchOpenCart = () => {
      if (typeof fetchCart === 'function') fetchCart();
      this.open();
    };
    document.addEventListener('dispatch-open-cart', this._onDispatchOpenCart);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onOpenClick);
    document.removeEventListener('cart-update', this._onCartUpdate);
    document.removeEventListener('dispatch-open-cart', this._onDispatchOpenCart);
  }

  _onOpenClick(e) {
    const btn = e.target.closest('[data-cart-sidebar], [data-cart-tab]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      this.open();
      btn.setAttribute('aria-expanded', 'true');
      if (typeof fetchCart === 'function') fetchCart();
    }
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.querySelectorAll('[data-cart-sidebar], [data-cart-tab]').forEach(btn => btn.setAttribute('aria-expanded', 'false'));
  }
}
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', HomenomCartDrawer);
}

class HomenomPredictiveSearch extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('.search-input');
    const results = this.querySelector('.predictive-search-results');
    if (!input || !results) return;

    input.addEventListener('focus', () => results.removeAttribute('hidden'));
    document.addEventListener('click', e => {
      if (!this.contains(e.target)) results.setAttribute('hidden', '');
    });
    input.addEventListener('input', debounce(async (e) => {
      const q = e.target.value.trim();
      if (q.length < 2) return;
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`);
      const data = await res.json();
      const liveDiv = this.querySelector('#LiveSearchResults');
      if (!liveDiv) return;
      liveDiv.innerHTML = data.resources.results.products.map(p =>
        `<a href="${p.url}" class="popular-item">
          <img src="${p.featured_image.url}" alt="${p.title}" style="width:50px;height:50px;object-fit:cover;border-radius:4px;">
          <div><span>${p.title}</span><small>${formatMoney(p.price)}</small></div>
        </a>`
      ).join('');
    }, 300));
  }
}
if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', HomenomPredictiveSearch);
}

class HomenomQuickViewModal extends HTMLElement {
  connectedCallback() {
    document.addEventListener('click', async e => {
      const btn = e.target.closest('[data-quick-view]');
      if (!btn) return;
      const handle = btn.dataset.quickView;
      const content = document.getElementById('QuickViewContent');
      if (content) content.innerHTML = '<p style="padding:40px;text-align:center;">Loading...</p>';
      this.setAttribute('aria-hidden', 'false');
      const res = await fetch(`/products/${handle}?section_id=product-quick-view`);
      const html = await res.text();
      if (content) content.innerHTML = html;
    });
    this.querySelectorAll('[data-close-modal]').forEach(btn =>
      btn.addEventListener('click', () => this.setAttribute('aria-hidden', 'true'))
    );
  }
}
if (!customElements.get('quick-view-modal')) {
  customElements.define('quick-view-modal', HomenomQuickViewModal);
}

class HomenomBackInStockModal extends HTMLElement {
  connectedCallback() {
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-notify-product]');
      if (!btn) return;
      this.setAttribute('aria-hidden', 'false');
      const titleEl = this.querySelector('[data-product-title]');
      if (titleEl) titleEl.textContent = btn.dataset.notifyTitle || '';
      this.querySelector('#BackInStockSubmit')?.addEventListener('click', async () => {
        const email = this.querySelector('#BackInStockEmail')?.value;
        const productId = btn.dataset.notifyProduct;
        if (email && window.routes) {
          // POST to back_in_stock_endpoint from settings or show success
          const success = this.querySelector('#BackInStockSuccess');
          if (success) { success.style.display = 'block'; }
        }
      }, { once: true });
    });
    this.querySelectorAll('[data-close-modal]').forEach(btn =>
      btn.addEventListener('click', () => this.setAttribute('aria-hidden', 'true'))
    );
  }
}
if (!customElements.get('back-in-stock-modal')) {
  customElements.define('back-in-stock-modal', HomenomBackInStockModal);
}

class HomenomVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.videos = this.querySelectorAll('video');
    this.initObserver();
    this.setupControls();
  }
  initObserver() {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
          this.videos.forEach(v => { if (v !== video) v.pause(); });
          video.play().catch(() => {});
          video.closest('.video-item')?.querySelector('.video-poster')?.style.setProperty('display', 'none');
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.6 });
    this.videos.forEach(v => obs.observe(v));
  }
  setupControls() {
    this.querySelectorAll('.video-item').forEach(item => {
      const video = item.querySelector('video');
      const playBtn = item.querySelector('.video-play-btn');
      const muteBtn = item.querySelector('.video-mute-btn');
      if (!video) return;
      playBtn?.addEventListener('click', () => {
        video.paused ? video.play() : video.pause();
        playBtn.textContent = video.paused ? '▶' : '⏸';
      });
      muteBtn?.addEventListener('click', () => {
        video.muted = !video.muted;
        muteBtn.textContent = video.muted ? '🔇' : '🔊';
      });
    });
  }
}
if (!customElements.get('video-carousel')) {
  customElements.define('video-carousel', HomenomVideoCarousel);
}

class HomenomFreeShippingComponent extends HTMLElement {
  connectedCallback() { this.update(0); }
  update(cartTotal) {
    const threshold = parseFloat(this.dataset.threshold) * 100;
    const remaining = Math.max(0, threshold - cartTotal);
    const pct = Math.min(100, (cartTotal / threshold) * 100);
    const bar = this.querySelector('.free-shipping-bar__progress-inner');
    const text = this.querySelector('.free-shipping-bar__text');
    if (bar) bar.style.width = pct + '%';
    if (text) {
      if (remaining <= 0) {
        text.innerHTML = window.free_shipping_achieved_message || '🎉 Free shipping unlocked!';
      } else {
        const msg = (window.free_shipping_message || 'Add {amount} more for free shipping!')
          .replace('{amount}', formatMoney(remaining));
        text.innerHTML = msg;
      }
    }
  }
}
if (!customElements.get('free-shipping-component')) {
  customElements.define('free-shipping-component', HomenomFreeShippingComponent);
}

// ─── Utilities ─────────────────────────────────────────────────────────────

function debounce(fn, wait) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}

function formatMoney(cents) {
  const format = window.money_format || 'Rs.{{amount}}';
  const amount = (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', amount);
}

async function fetchCart() {
  const res = await fetch('/cart.json');
  const cart = await res.json();
  document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = cart.item_count);
  document.querySelectorAll('[data-cart-subtotal]').forEach(el => el.textContent = formatMoney(cart.total_price));
  document.querySelectorAll('free-shipping-component').forEach(el => el.update(cart.total_price));
  renderCartItems(cart);
}

function renderCartItems(cart) {
  const containers = document.querySelectorAll('#CartItems, #CartPageItems');
  
  const html = cart.item_count === 0 
    ? '<p class="cart-empty" style="text-align:center;padding:40px 20px;color:#666;">Your cart is empty</p>' 
    : cart.items.map(item => `
    <div class="cart-item">
      <img src="${item.image}" alt="${item.title}" class="cart-item__image" loading="lazy">
      <div class="cart-item__info">
        <p class="cart-item__title">${item.product_title}</p>
      
        <div class="cart-item__price-row">
          <div class="quantity-selector-wrapper">
            <div class="quantity-selector" style="display:flex;align-items:center;gap:0;border:none;background:transparent;">
              <button class="quantity-minus" onclick="changeQty('${item.key}', ${item.quantity - 1})" type="button">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12h14"/></svg>
              </button>
              <input type="number" value="${item.quantity}" readonly style="width:36px;text-align:center;border:none;background:transparent;font-weight:700;">
              <button class="quantity-plus" onclick="changeQty('${item.key}', ${item.quantity + 1})" type="button">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M12 5v14M5 12h14"/></svg>
              </button>
            </div>
          </div>
          <span style="font-weight:800; color:#db741b; font-size:16px;">${formatMoney(item.final_line_price)}</span>
        </div>
      </div>
    </div>
  `).join('');

  containers.forEach(container => {
    container.innerHTML = html;
  });

  const cartPageFull = document.getElementById('CartPageFull');
  const cartPageEmpty = document.getElementById('CartPageEmpty');
  if (cartPageFull && cartPageEmpty) {
    if (cart.item_count === 0) {
      cartPageFull.style.display = 'none';
      cartPageEmpty.style.display = 'block';
    } else {
      cartPageFull.style.display = 'block';
      cartPageEmpty.style.display = 'none';
    }
  }
}

async function addToCart(variantId, quantity = 1) {
  await fetch('/cart/add.json', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity })
  });
  await fetchCart();
  // Try both the custom and base theme cart drawer open methods
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open();
  }
}

async function changeQty(key, qty) {
  await fetch('/cart/change.json', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: key, quantity: qty })
  });
  fetchCart();
}

// ─── Scroll Trigger Animations ──────────────────────────────────────────────

const scrollObs = new IntersectionObserver((entries) => {
  entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('active'); });
}, { threshold: 0.1 });
document.querySelectorAll('.scroll-trigger').forEach(el => scrollObs.observe(el));

// ─── Init on DOM Ready ──────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  fetchCart();

  // Mobile nav active state
  document.querySelectorAll('.mobile-nav-bar__tab').forEach(tab => {
    tab.classList.toggle('active', tab.getAttribute('href') === window.location.pathname);
  });

  // Footer accordion on mobile
  document.querySelectorAll('.footer__heading--accordion').forEach(heading => {
    heading.addEventListener('click', () => {
      const content = heading.nextElementSibling;
      heading.classList.toggle('open');
      content.style.display = content.style.display === 'none' ? 'block' : 'none';
    });
    // Collapsed by default on mobile
    if (window.innerWidth < 768) heading.nextElementSibling.style.display = 'none';
  });

  // Patch base theme's product-form-component add-to-cart to open our cart drawer.
  // The Saver theme's product-form.js uses fetch() and then dispatches events.
  // We intercept by monkey-patching the cart add URL fetch calls.
  const _origFetch = window.fetch;
  window.fetch = function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
    return _origFetch.apply(this, args).then(response => {
      if (url.includes('/cart/add')) {
        // Clone so the original consumer can still read it
        response.clone().json().then(data => {
          if (data && (data.id || data.items)) {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              fetchCart().then(() => cartDrawer.open());
            }
          }
        }).catch(() => {});
      }
      return response;
    });
  };
  // Add to Cart from product grid
  document.addEventListener('click', e => {
    const atcBtn = e.target.closest('[data-atc]');
    if (atcBtn) {
      e.preventDefault();
      const variantId = atcBtn.dataset.atc;
      atcBtn.classList.add('loading');
      atcBtn.disabled = true;
      addToCart(variantId, 1).finally(() => {
        atcBtn.classList.remove('loading');
        atcBtn.disabled = false;
      });
    }
  });
});
